import { Component, EventEmitter, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';

import { ConfigService } from '../../_services/config.service';
import { NotificationsService } from '../_services/notifications.service';
import { NotifyPreference, UserNotify } from '../_models/notification.model';

/**
 * Pannello compatto delle notifiche recenti, pensato per essere mostrato
 * dentro un p-popover ancorato alla campanella in header.
 * Tutte le chiamate sono in background (X-No-Spinner): nessuno spinner globale.
 *
 * NB: il pannello è contenuto proiettato dentro p-popover, che è OnPush. Quando arriva una
 * risposta HTTP Angular non ridiscende nel popover finché un evento (click, scroll) non lo
 * sporca: senza markForCheck() la lista restava sullo spinner anche a chiamata conclusa.
 * Ogni callback asincrona chiama quindi this.cdr.markForCheck().
 */
@Component({
	standalone: true,
	selector: 'app-notifications-panel',
	templateUrl: './notifications-panel.component.html',
	styleUrls: ['./notifications-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CommonModule, FormsModule, TranslatePipe, ButtonModule, SelectButtonModule],
})
export class NotificationsPanelComponent {
	/** Emesso quando il pannello vuole chiudere il popover (es. dopo navigazione). */
	@Output() closed = new EventEmitter<void>();
	/** Emesso quando una notifica viene letta (per aggiornare il badge in header). */
	@Output() unreadChanged = new EventEmitter<void>();

	list: UserNotify[] = [];
	loading: boolean = false;
	markingAll: boolean = false;

	/** Notifica espansa inline nel pannello (null = vista lista). */
	selected: UserNotify | null = null;

	/** Solo gli amministratori possono accedere alla lista completa. */
	isAdmin: boolean = false;

	/** Preferenze canale dell'utente (le stesse del profilo), caricate a ogni apertura del pannello. */
	preferences: NotifyPreference[] = [];
	prefSaving: boolean = false;
	prefSaved: boolean = false;
	/** Stesse etichette/icone della sezione "Preferenze notifiche" del profilo. */
	channelOptions = [
		{ label: 'profile.notify.channel.email', value: 'email', icon: 'pi pi-envelope' },
		{ label: 'profile.notify.channel.portal', value: 'portal', icon: 'pi pi-bell' },
		{ label: 'profile.notify.channel.none', value: 'none', icon: 'pi pi-ban' },
	];

	constructor(
		private router: Router,
		private configService: ConfigService,
		private notificationsService: NotificationsService,
		private cdr: ChangeDetectorRef,
	) {
		this.isAdmin = this.configService.isUserSuperAdmin();
	}

	/** Chiamato dal popover all'apertura. */
	load(): void {
		this.selected = null;
		this.prefSaved = false;
		this.loading = true;
		this.notificationsService.GetRecentNotifications(15).subscribe({
			next: (data) => {
				this.list = data || [];
				this.loading = false;
				this.cdr.markForCheck();
			},
			error: () => {
				this.loading = false;
				this.cdr.markForCheck();
			},
		});
		this.notificationsService.GetMyNotifyPreferencesSilent(this.configService.getCompany_id()).subscribe({
			next: (prefs) => {
				this.preferences = prefs || [];
				this.cdr.markForCheck();
			},
			error: () => {},
		});
	}

	isUnread(item: UserNotify): boolean {
		return !item.usrnot_dateread;
	}

	get unreadCount(): number {
		return this.list.filter((item) => this.isUnread(item)).length;
	}

	/** Apertura inline: segna come letta ed espande il contenuto nel pannello. */
	onItemClick(item: UserNotify): void {
		if (this.isUnread(item)) {
			this.notificationsService.MarkAsReadSilent(item.usrnot_id).subscribe(() => {
				item.usrnot_dateread = new Date().toISOString();
				this.unreadChanged.emit();
				this.cdr.markForCheck();
			});
		}
		this.prefSaved = false;
		this.selected = item;
	}

	/** Segna lette tutte le notifiche dell'utente (anche quelle oltre le 15 mostrate: è scoped lato server). */
	markAllRead(): void {
		if (this.markingAll) return;
		this.markingAll = true;
		this.notificationsService.MarkAllAsReadSilent().subscribe({
			next: () => {
				const now = new Date().toISOString();
				this.list = this.list.map((item) => (item.usrnot_dateread ? item : { ...item, usrnot_dateread: now }));
				this.markingAll = false;
				this.unreadChanged.emit();
				this.cdr.markForCheck();
			},
			error: () => {
				this.markingAll = false;
				this.cdr.markForCheck();
			},
		});
	}

	/** Torna alla lista dalla vista espansa. */
	back(): void {
		this.selected = null;
	}

	/** Admin: vai alla lista completa delle notifiche. */
	goToList(): void {
		this.closed.emit();
		this.router.navigate(['/notifications/lista']);
	}

	/** Sezione "Preferenze notifiche" del profilo (per i tipi senza preferenza puntuale). */
	goToProfile(): void {
		this.closed.emit();
		this.router.navigate(['/profile']);
	}

	/**
	 * Preferenza canale corrispondente alla notifica aperta: usrnot_type uguale a umst_value
	 * (es. rfi_ticket_apertura). I tipi con suffisso ":id" (APPOINTMENT:20) sono confrontati sulla
	 * parte prima dei due punti. null = tipo senza preferenza configurabile (mittente che non
	 * consulta user_mail_settings).
	 */
	get selectedPref(): NotifyPreference | null {
		if (!this.selected) return null;
		const type = (this.selected.usrnot_type || '').split(':')[0].trim().toLowerCase();
		if (!type) return null;
		return this.preferences.find((p) => (p.umst_value || '').toLowerCase() === type) || null;
	}

	/** Salvataggio immediato della scelta (stesso endpoint del profilo, upsert del singolo tipo). */
	onChannelChange(pref: NotifyPreference): void {
		this.prefSaving = true;
		this.prefSaved = false;
		this.notificationsService.SaveMyNotifyPreferenceSilent(pref.umst_id, pref.effective_channel).subscribe({
			next: () => {
				pref.ums_channel = pref.effective_channel;
				this.prefSaving = false;
				this.prefSaved = true;
				this.cdr.markForCheck();
			},
			error: () => {
				// Ripristina la scelta precedente (o il default) per non mostrare uno stato non salvato
				pref.effective_channel = pref.ums_channel || pref.umst_default_channel || 'email';
				this.prefSaving = false;
				this.cdr.markForCheck();
			},
		});
	}

	getChannelLabel(item: UserNotify): string {
		if (item.usrnot_typesent === 'M') return 'Email';
		if (item.usrnot_typesent === 'S') return 'SMS';
		return 'Push';
	}

	getImageUrl(filename: string): string {
		return this.notificationsService.GetNotificationImageUrl(filename);
	}

	getMessage(item: UserNotify): string {
		const msg = item.usrnot_message_it || item.usrnot_message_en || '';
		const plain = msg.replace(/<[^>]*>/g, '').trim();
		return plain.length > 90 ? plain.substring(0, 90) + '…' : plain;
	}

	getTimeAgo(dateStr: string | null): string {
		if (!dateStr) return '';
		const date = new Date(dateStr);
		const now = new Date();
		const diff = now.getTime() - date.getTime();
		const minutes = Math.floor(diff / 60000);
		if (minutes < 1) return 'ora';
		if (minutes < 60) return `${minutes}m`;
		const hours = Math.floor(minutes / 60);
		if (hours < 24) return `${hours}h`;
		const days = Math.floor(hours / 24);
		if (days < 7) return `${days}g`;
		return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
	}

	getIcon(item: UserNotify): string {
		if (item.usrnot_typesent === 'M') return 'pi pi-envelope';
		if (item.usrnot_typesent === 'S') return 'pi pi-mobile';
		return 'pi pi-bell';
	}
}
